import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, PackageCheck, PackageSearch } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useStoreIndent, useIssueStoreIndent, useStoreLookups, useCheckStoreIndentStock } from "@/features/store-indents/api";
import { resolveLocationPath, type LocationNode } from "@/features/store-indents/LocationHierarchyPicker";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { fmtDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/store-indents/$id")({
  component: StoreIndentDetail,
});

type StockInfo = {
  total_on_hand: number;
  total_reserved: number;
  available: number;
  stores: Array<{ store_location: string; branch_id: string | null; qty_on_hand: number; qty_reserved: number; available: number }>;
};

function StoreIndentDetail() {
  const matchRoute = useMatchRoute();
  const isExact = matchRoute({ to: "/store-indents/$id", fuzzy: false });

  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useStoreIndent(id);
  const { data: lookups } = useStoreLookups();
  const issue = useIssueStoreIndent();
  const { data: me } = useCurrentUser();
  const canIssue = (me?.roles ?? []).some((r) => r === "purchase_stores" || r === "super_admin");
  const locationNodes = (lookups?.locations ?? []) as LocationNode[];

  const [toIssue, setToIssue] = useState<Record<string, number>>({});
  const [stock, setStock] = useState<Record<string, StockInfo>>({});
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [uncheckedCount, setUncheckedCount] = useState(0);

  useEffect(() => {
    if (data?.items) {
      const init: Record<string, number> = {};
      for (const it of data.items) {
        const pending = Number(it.qty_requested) - Number(it.qty_issued);
        init[it.id] = pending > 0 ? pending : 0;
      }
      setToIssue(init);
      setStock({});
    }
  }, [data?.id]); // eslint-disable-line

  const isAsset = data?.kind === "asset";
  const pendingSpareItems = useMemo(
    () => (data?.items ?? []).filter((it) => (it.spare?.id ?? null) && Number(it.qty_requested) - Number(it.qty_issued) > 0),
    [data?.items],
  );

  const checkStockMutation = useCheckStoreIndentStock();

  const checkStock = async (spareId: string, itemId: string) => {
    // Left for compatibility with the row-level button, but it now checks all stock
    await checkAllStock();
  };

  const checkAllStock = async () => {
    if (pendingSpareItems.length === 0 || !data?.id) return;
    
    // Set all pending items to checking state
    const newChecking: Record<string, boolean> = {};
    for (const it of pendingSpareItems) newChecking[it.id] = true;
    setChecking((s) => ({ ...s, ...newChecking }));
    
    try {
      const results = await checkStockMutation.mutateAsync(data.id);
      
      const newStock: Record<string, StockInfo> = {};
      const newToIssue: Record<string, number> = {};
      
      for (const res of results) {
        const itemId = res.item_id;
        const info = res as StockInfo;
        newStock[itemId] = info;
        
        // Find the requested quantity for this item
        const item = pendingSpareItems.find(it => it.id === itemId);
        if (item) {
          const requested = Number(item.qty_requested) - Number(item.qty_issued);
          newToIssue[itemId] = Math.min(requested, info.available);
        }
      }
      
      setStock((s) => ({ ...s, ...newStock }));
      setToIssue((s) => ({ ...s, ...newToIssue }));
      toast.success("Stock refreshed for all lines");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      // Clear checking state
      const clearedChecking: Record<string, boolean> = {};
      for (const it of pendingSpareItems) clearedChecking[it.id] = false;
      setChecking((s) => ({ ...s, ...clearedChecking }));
    }
  };

  if (isLoading || !data) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const kindLabel = data.kind === "asset" ? "Asset Allocation" : data.kind === "consumable" ? "Consumable" : "Spare";

  const onIssue = async () => {
    // Guard: require stock check for all pending spare lines
    const unchecked = pendingSpareItems.filter((it) => !stock[it.id]);
    if (unchecked.length > 0) {
      setUncheckedCount(unchecked.length);
      setShowConfirm(true);
      return;
    }
    await processIssue();
  };

  const processIssue = async () => {
    // Guard: any qty over available
    const overIssue = pendingSpareItems.find((it) => {
      const info = stock[it.id];
      const q = toIssue[it.id] ?? 0;
      return info && q > info.available;
    });
    if (overIssue) return toast.error("One or more lines exceed available stock");

    const lines = Object.entries(toIssue)
      .filter(([, q]) => q > 0)
      .map(([store_indent_item_id, qty_issued]) => ({ store_indent_item_id, qty_issued }));
    if (lines.length === 0) return toast.error("Enter quantities to issue");
    try {
      await issue.mutateAsync({ storeIndentId: data.id, lines });
      toast.success(isAsset ? "Asset allocated" : "Issued");
      setStock({});
      setToIssue({});
    } catch (e) { toast.error((e as Error).message); }
  };

  const canDoIssue = canIssue && data.status !== "Issued" && data.status !== "Rejected" && data.status !== "Closed";

  if (!isExact) {
    return <Outlet />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{data.store_indent_no}</h1>
            <Badge variant="secondary">{data.status}</Badge>
            <Badge>{kindLabel}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {data.branch?.name} · {data.department?.name}
            {data.source_store_name && ` · Source Store: ${data.source_store_name}`}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Requested by <span className="font-medium">{data.requester?.full_name || data.requester?.email || "—"}</span>
            {" · "}Raised {fmtDateTime(data.created_at)}
          </p>
        </div>
        <div className="flex gap-2">
          {!isAsset && canDoIssue && pendingSpareItems.length > 0 && (
            <Button variant="outline" onClick={checkAllStock}>
              <PackageSearch className="h-4 w-4 mr-1" />Check All Stock
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate({ to: "/store-indents" })}>Back</Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Item</TableHead>
              {isAsset && <TableHead>Destination</TableHead>}
              {!isAsset && <TableHead className="text-right">Requested</TableHead>}
              {!isAsset && <TableHead className="text-right">Issued</TableHead>}
              {!isAsset && <TableHead className="text-right">Pending</TableHead>}
              {!isAsset && <TableHead>Available</TableHead>}
              {isAsset && <TableHead>Status</TableHead>}
              {canDoIssue && <TableHead className="text-right w-52">{isAsset ? "Allocate" : "Issue Now"}</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {(data.items ?? []).map((it) => {
                const pending = Number(it.qty_requested) - Number(it.qty_issued);
                const info = stock[it.id];
                const short = info && pending > info.available;
                const empty = info && info.available <= 0;
                const rowClass = empty ? "bg-destructive/5" : short ? "bg-amber-500/5" : "";
                return (
                  <TableRow key={it.id} className={rowClass}>
                    <TableCell>
                      {isAsset
                        ? (it.asset ? `${it.asset.asset_code} · ${it.asset.name}` : it.item_name)
                        : (it.spare?.name ?? it.item_name)}
                    </TableCell>
                    {isAsset && <TableCell className="text-xs">{resolveLocationPath(locationNodes, it.location_id)}</TableCell>}
                    {!isAsset && <TableCell className="text-right tabular-nums">{Number(it.qty_requested).toFixed(2)}</TableCell>}
                    {!isAsset && <TableCell className="text-right tabular-nums">{Number(it.qty_issued).toFixed(2)}</TableCell>}
                    {!isAsset && <TableCell className="text-right tabular-nums">{pending.toFixed(2)}</TableCell>}
                    {!isAsset && (
                      <TableCell>
                        {info ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="text-left">
                                <Badge variant={empty ? "destructive" : short ? "secondary" : "default"}>
                                  {info.available.toFixed(2)} avail
                                </Badge>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72">
                              <div className="text-xs space-y-2">
                                <div className="flex justify-between"><span>On hand</span><b>{info.total_on_hand}</b></div>
                                <div className="flex justify-between"><span>Reserved</span><b>{info.total_reserved}</b></div>
                                <div className="flex justify-between"><span>Available</span><b>{info.available}</b></div>
                                {info.stores.length > 0 && (
                                  <>
                                    <div className="mt-2 font-medium">Per store</div>
                                    <div className="space-y-1">
                                      {info.stores.map((s, i) => (
                                        <div key={i} className="flex justify-between border-b py-1">
                                          <span className="truncate">{s.store_location}</span>
                                          <span>{s.available}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : it.spare?.id ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={checking[it.id]}
                            onClick={() => checkStock(it.spare!.id, it.id)}
                          >
                            {checking[it.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <PackageSearch className="h-3 w-3 mr-1" />}
                            Check
                          </Button>
                        ) : (
                          <button 
                            type="button"
                            className="text-muted-foreground hover:text-foreground flex items-center text-xs"
                            onClick={() => it.spare?.id && checkStock(it.spare.id, it.id)}
                            disabled={checking[it.id]}
                          >
                            {checking[it.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <PackageSearch className="h-3 w-3 mr-1" />}
                            Check
                          </button>
                        )}
                      </TableCell>
                    )}
                    {isAsset && (
                      <TableCell>
                        <Badge variant={pending > 0 ? "secondary" : "default"}>{pending > 0 ? "Pending" : "Allocated"}</Badge>
                      </TableCell>
                    )}
                    {canDoIssue && (
                      <TableCell className="text-right">
                        {isAsset ? (
                          pending <= 0 || issue.isPending ? (
                            <Button size="sm" disabled>Allocate</Button>
                          ) : (
                            <Button size="sm" asChild>
                              <Link to="/store-indents/$id/allocate/$itemId" params={{ id: data.id, itemId: it.id }}>
                                Allocate
                              </Link>
                            </Button>
                          )
                        ) : (
                          <Input type="number" step="1" min={0} max={info ? info.available : pending}
                            value={toIssue[it.id] !== undefined ? toIssue[it.id] : ""}
                            onKeyDown={(e) => {
                              if (e.key === "-" || e.key === "e" || e.key === "." || e.key === ",") e.preventDefault();
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") {
                                setToIssue((s) => ({ ...s, [it.id]: "" as any }));
                                return;
                              }
                              const parsed = parseInt(val, 10);
                              if (!Number.isNaN(parsed)) {
                                setToIssue((s) => ({ ...s, [it.id]: Math.max(0, parsed) }));
                              }
                            }}
                            className="w-28 ml-auto" />
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {canDoIssue && !isAsset && (
        <div className="flex justify-end">
          <Button onClick={onIssue} disabled={issue.isPending}><PackageCheck className="h-4 w-4 mr-1" />Record Issue</Button>
        </div>
      )}

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unchecked Stock</AlertDialogTitle>
            <AlertDialogDescription>
              {uncheckedCount} line(s) have not been stock-checked. Continue with recording issue anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowConfirm(false);
              processIssue();
            }}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
